import React, { useMemo, useState } from 'react';
import { Button, Space, Tag, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { ResponsiveTable } from '@src/components/Table';
import { FSCollections } from '@src/constants/database';
import type { CommonReduxState } from '@src/models/commons/reduxState.interface';
import type { AuditLog } from '@src/models/interfaces/AuditLog/AuditLogInterface';
import {
  formatAuditChangeValue,
  getAuditChangeRows,
  getAuditChangedFieldLabels,
  getAuditFieldLabel,
  getAuditLogEntityName,
  getAuditLogMessage,
  isValidAuditLogActor,
  resolveAuditLogDivisionName,
} from '@src/packages/utils/auditLogs';
import { AUDIT_DATE_FORMAT, DateUtil } from '@src/packages/utils/dates';

import styles from './styles.module.less';

interface AuditLogTableProps {
  logs: AuditLog[];
  loading?: boolean;
  maxHeight?: string | number;
  /** When true (default), show entity origin Tag under the message. Omit in entity-scoped viewers. */
  showEntityLabel?: boolean;
}

const { Text } = Typography;

type AssignedDivisions = CommonReduxState['divisions']['assigned']['data']['divisions'];

const EMPTY_DIVISIONS: AssignedDivisions = {};

const AuditChangeDetails: React.FC<{ log: AuditLog }> = ({ log }) => {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);
  const rows = getAuditChangeRows(log.userFacingChanges);

  if (!rows.length) {
    return null;
  }

  return (
    <div className={styles.changes}>
      <Button
        className={styles.changesToggle}
        onClick={() => setExpanded(current => !expanded)}
        size="small"
        type="link"
      >
        {intl.formatMessage({
          id: expanded ? 'auditLogs.changes.hideDetails' : 'auditLogs.changes.showDetails',
        })}
      </Button>
      {expanded ? (
        <div className={styles.changesList}>
          {rows.map(change => (
            <div className={styles.changeRow} key={`${log.id}-${change.field}`}>
              <Text strong>{getAuditFieldLabel(intl, change.field)}</Text>
              {change.kind === 'redacted' ? (
                <Text>{formatAuditChangeValue(intl, change, 'new', log.extraKeys.timeZone)}</Text>
              ) : (
                <>
                  <Text type="secondary">
                    {intl.formatMessage({ id: 'auditLogs.changes.previous' })}:{' '}
                    {formatAuditChangeValue(intl, change, 'old', log.extraKeys.timeZone)}
                  </Text>
                  <Text>
                    {intl.formatMessage({ id: 'auditLogs.changes.current' })}:{' '}
                    {formatAuditChangeValue(intl, change, 'new', log.extraKeys.timeZone)}
                  </Text>
                </>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const AuditLogTable: React.FC<AuditLogTableProps> = ({
  logs,
  loading = false,
  maxHeight = 'none',
  showEntityLabel = true,
}) => {
  const intl = useIntl();
  const divisions = useSelector(
    (state: CommonReduxState) => state?.divisions?.assigned?.data?.divisions ?? EMPTY_DIVISIONS,
  );

  const entityLabels = useMemo<Record<string, string>>(
    () => ({
      [FSCollections.SCHEDULES]: intl.formatMessage({ id: 'sidebar.menu.schedules' }),
      [FSCollections.WORK_JOURNEY_CONFIGS]: intl.formatMessage({
        id: 'sidebar.menu.work.journey',
      }),
      [FSCollections.EVENT_TYPES]: intl.formatMessage({ id: 'sidebar.menu.forms' }),
      [FSCollections.FORMS]: intl.formatMessage({ id: 'sidebar.menu.forms' }),
      [FSCollections.TRIGGERS]: intl.formatMessage({ id: 'sidebar.menu.triggers' }),
      [FSCollections.USERS]: intl.formatMessage({ id: 'sidebar.configuration.users' }),
      [FSCollections.USER_JOBS]: intl.formatMessage({ id: 'sidebar.configuration.job' }),
      [FSCollections.WORK_SHIFTS]: intl.formatMessage({ id: 'shifts.title' }),
      [FSCollections.SCHEDULE_TEMPLATES]: intl.formatMessage({
        id: 'sidebar.menu.work.templates',
      }),
      [FSCollections.LOCATIONS]: intl.formatMessage({
        id: 'sidebar.configuration.locations',
      }),
      [FSCollections.LOCATION_GROUPS]: intl.formatMessage({
        id: 'general.locationsGroup',
      }),
      [FSCollections.GROUPS]: intl.formatMessage({ id: 'groups.list.title' }),
      [FSCollections.HOLIDAYS]: intl.formatMessage({
        id: 'sidebar.configuration.holidays',
      }),
      [FSCollections.EXCEPTIONS]: intl.formatMessage({
        id: 'sidebar.configuration.holidays',
      }),
      [FSCollections.ADDRESSES]: intl.formatMessage({ id: 'addresses.title' }),
      [FSCollections.DIVISIONS]: intl.formatMessage({
        id: 'sidebar.configuration.divisions',
      }),
      [FSCollections.COMPANIES]: intl.formatMessage({ id: 'externalCompanies.title' }),
      [FSCollections.EXECUTIONS]: intl.formatMessage({ id: 'general.executions' }),
      [FSCollections.EVENTS]: intl.formatMessage({ id: 'sidebar.dashboards.events' }),
      [FSCollections.EMERGENCIES]: intl.formatMessage({ id: 'emergencies.menu.title' }),
      work_shift_policies: intl.formatMessage({ id: 'auditLogs.entities.work_shift_policies' }),
      notification_subscribers: intl.formatMessage({
        id: 'auditLogs.entities.notification_subscribers',
      }),
    }),
    [intl],
  );

  const columns = useMemo(
    () => [
      {
        key: 'createdAt',
        dataIndex: 'createdAt',
        title: intl.formatMessage({ id: 'auditLogs.columns.dateTime' }),
        width: 190,
        render: (log: AuditLog) =>
          DateUtil.toDateString(log.createdAt, AUDIT_DATE_FORMAT, log.extraKeys.timeZone ?? null),
      },
      {
        key: 'description',
        dataIndex: 'description',
        title: intl.formatMessage({ id: 'auditLogs.columns.description' }),
        render: (log: AuditLog) => (
          <Space direction="vertical" size={4}>
            <Text>
              {getAuditLogMessage(intl, log.changeCategory, log.description, {
                entityName: getAuditLogEntityName(log) || log.entityName,
                includeEntityName: showEntityLabel,
                divisionName: showEntityLabel
                  ? resolveAuditLogDivisionName(log.divisionId, log.entityType, divisions)
                  : undefined,
                changeFieldsSummary:
                  getAuditChangedFieldLabels(intl, log.userFacingChanges) || null,
              })}
            </Text>
            <AuditChangeDetails log={log} />
            {showEntityLabel ? (
              <Tag color="blue">{entityLabels[log.entityType] ?? log.entityType}</Tag>
            ) : null}
          </Space>
        ),
      },
      {
        key: 'user',
        dataIndex: 'user',
        title: intl.formatMessage({ id: 'auditLogs.columns.user' }),
        width: 260,
        render: (log: AuditLog) => {
          if (!isValidAuditLogActor(log.user) || !log.user) {
            return intl.formatMessage({ id: 'auditLogViewer.systemProcess' });
          }

          return (
            <Space direction="vertical" size={0}>
              <Text>{log.user.name}</Text>
              {log.user.email ? <Text type="secondary">{log.user.email}</Text> : null}
            </Space>
          );
        },
      },
    ],
    [divisions, entityLabels, intl, showEntityLabel],
  );

  return (
    <ResponsiveTable<AuditLog>
      columns={columns}
      dataSource={logs}
      loading={loading}
      maxHeight={maxHeight}
      stickyHeader={maxHeight !== 'none'}
    />
  );
};

export default React.memo(AuditLogTable);
